import React from 'react';

import { CALL_MODES, ROLE_LABELS, STATES } from '../domain/constants';
import { clubLogo } from '../domain/clubs';

// La banda alta dello schermo da proiettare.
//
// Non riusa LotCard: quello monta la barra delle offerte, il modulo del
// rilancio automatico e un tetto di spesa *personale* — con `team` assente
// stamperebbe «Puoi arrivare a 0» a tutta la stanza. Qui si riusano le
// derivazioni e l'idioma `--clock`, non il componente.

function nameOf(participants, id) {
    return participants.find((participant) => participant.id === id)?.name ?? null;
}

function surnameOf(player, fallback) {
    return player?.Cognome || player?.['Nome Completo'] || fallback;
}

export default function TvLot({ auction, catalogue, remainingSeconds, live }) {
    const { lot, state, participants } = auction;

    if (live && lot) {
        const settled = state === STATES.AWAITING_CONFIRM;
        const player = catalogue[lot.player_id];
        const leader = nameOf(participants, lot.leading_participant_id);
        const auctionTimer = auction.settings?.auction_timer_x ?? 0;
        // Stessa formula di LotCard: la barra e il numero scoloriscono insieme.
        const clock = auctionTimer
            ? Math.min(100, Math.max(0, (remainingSeconds / auctionTimer) * 100))
            : 100;

        return (
            <section
                className={`tv-lot${settled ? ' tv-lot--settled' : ''}`}
                style={{ '--clock': clock }}
                aria-label="Giocatore in asta"
            >
                <div className="lot-clock">
                    <div
                        className="lot-clock-bar"
                        role="progressbar"
                        aria-label="Tempo rimanente"
                        aria-valuenow={settled ? 0 : remainingSeconds}
                        aria-valuemin={0}
                        aria-valuemax={auctionTimer}
                    />
                </div>

                {player?.['URL Immagine'] && (
                    <img className="tv-lot-photo" src={player['URL Immagine']} alt="" />
                )}

                {/* Una griglia sola per nome, prezzo e vincitore: prima erano
                    tre blocchi affiancati, ognuno centrato per conto suo, e le
                    tre scritte grandi finivano a tre altezze diverse. Adesso
                    condividono le righe — occhielli sopra, testo grande sulla
                    stessa linea di base, dettagli sotto. */}
                <div className="tv-lot-grid">
                    <p className="tv-eyebrow tv-lot-eyebrow">
                        {settled ? 'Asta chiusa' : 'In asta'}
                    </p>
                    <h2 className="tv-lot-name">{surnameOf(player, lot.player_name)}</h2>
                    <p className="tv-lot-meta">
                        <span className="badge badge--role" data-role={lot.role}>
                            {lot.role}
                        </span>
                        {player?.Squadra && (
                            <span className="tv-club">
                                {clubLogo(player.Squadra) && (
                                    <img src={clubLogo(player.Squadra)} alt="" />
                                )}
                                {player.Squadra}
                            </span>
                        )}
                    </p>

                    <span className="tv-lot-price num">{lot.current_bid}</span>

                    <p className="tv-eyebrow tv-lot-leader-label">
                        {settled ? 'Aggiudicato a' : 'In testa'}
                    </p>
                    <span className="tv-lot-leader-name">{leader ?? 'Nessuno'}</span>

                    {/* A timer fermo il numero sparisce invece di restare a
                        zero: uno zero gigante sembra un'asta ancora viva. */}
                    {!settled && (
                        <p className="tv-clock num">
                            {remainingSeconds}
                            <span className="tv-clock-unit">s</span>
                        </p>
                    )}
                </div>
            </section>
        );
    }

    return (
        <section className="tv-lot tv-lot--compact" aria-label="Stato dell'asta">
            <Status auction={auction} catalogue={catalogue} />
        </section>
    );
}

// La riga che resta quando il timer e fermo: dice cosa si sta aspettando,
// mentre lo spazio va alle rose.
function Status({ auction, catalogue }) {
    const { state, participants, pending_pick: pick } = auction;
    const roleLabel = ROLE_LABELS[auction.current_phase_role] ?? '';

    if (state === STATES.FINISHED) {
        return <p className="tv-status">Asta conclusa</p>;
    }

    if (state === STATES.SETUP || state === STATES.PHASE_BREAK) {
        return (
            <p className="tv-status">
                {state === STATES.SETUP
                    ? "In attesa che il banditore avvii l'asta"
                    : `Si riparte con i ${roleLabel.toLowerCase()}`}
            </p>
        );
    }

    if (pick && auction.call_mode && auction.call_mode !== CALL_MODES.FREE) {
        const player = catalogue[pick.player_id];
        return (
            <p className="tv-status">
                <span className="badge badge--role" data-role={pick.role}>
                    {pick.role}
                </span>
                <span className="tv-status-name">
                    {surnameOf(player, pick.player_name)}
                </span>
                <span className="tv-status-note">in attesa del via del banditore</span>
            </p>
        );
    }

    const turn = nameOf(participants, auction.current_turn_participant_id);
    return (
        <p className="tv-status">
            Tocca a <span className="tv-status-name">{turn ?? '—'}</span>
        </p>
    );
}
